// Credit notes register (BUILD_SPEC.md §11, §8.4 step 5). Plan.md G23.
import { useState } from 'react';
import { Button, Group, Select, Stack, Table } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconFileInvoice } from '@tabler/icons-react';
import { formatMoneyCents } from './format';
import { useCreditNotesList, useInvoice } from './api';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';

const STATUS_OPTIONS = ['DRAFT', 'APPROVED', 'SYNCED', 'VOID'] as const;

/** Resolves an invoice id to its own doc number for display (react-query dedupes
 * repeat ids into one request). */
function InvoiceNumber({ id }: { id: string }) {
  const { data, isLoading } = useInvoice(id);
  return <>{data ? data.number : isLoading ? '…' : id}</>;
}

export function CreditNotesRegister() {
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number] | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useCreditNotesList({ cursor, status });

  return (
    <Stack gap="md">
      <PageHeader
        title="Credit notes"
        description="Credits issued against an invoice, with or without restocking the goods — the last step when something comes back: sales order → dispatch → invoice → credit note."
        actions={
          <Select
            placeholder="All statuses"
            clearable
            data={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            value={status ?? null}
            onChange={(v) => {
              setStatus((v as (typeof STATUS_OPTIONS)[number]) ?? undefined);
              setCursors([undefined]);
            }}
            w={160}
          />
        }
      />

      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Invoice</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Restock</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.items ?? []).map((cn) => (
              <Table.Tr key={cn.id}>
                <Table.Td>
                  <Link to="/sales-invoicing/credit-notes/$creditNoteId" params={{ creditNoteId: cn.id }}>
                    {cn.number}
                  </Link>
                </Table.Td>
                <Table.Td>
                  <Link to="/sales-invoicing/invoices/$invoiceId" params={{ invoiceId: cn.invoiceId }}>
                    <InvoiceNumber id={cn.invoiceId} />
                  </Link>
                </Table.Td>
                <Table.Td>{cn.reason ?? '—'}</Table.Td>
                <Table.Td>{cn.restock ? 'Yes' : 'No'}</Table.Td>
                <Table.Td>
                  <DocStatusBadge status={cn.status} domain="creditNote" />
                </Table.Td>
                <Table.Td ta="right">{formatMoneyCents(cn.totalCents)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <EmptyState
                    icon={IconFileInvoice}
                    title="No credit notes yet"
                    hint="Raise a credit note from the invoice it corrects — open an invoice and use 'Create credit note'."
                    action={
                      <Button component={Link} to="/sales-invoicing/invoices" variant="light">
                        Go to Invoices
                      </Button>
                    }
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
